import React from 'react';
import RowCheckbox from '../../app/components/RowCheckbox';
import type { Vehicle } from '../../app/models/Vehicle';
import type { FitmentFieldDefinition } from '../../app/models/FitmentCatalog';
import { getLegacyVehicleFieldValue, normalizeFitmentKey } from '../../app/utils/fitmentFields';

export const catalogColumns = (fields: FitmentFieldDefinition[]) => {
    const combined = fields.some(f => normalizeFitmentKey(f) === 'make')
        && fields.some(f => normalizeFitmentKey(f) === 'model');
    if (!combined) return fields;
    const identity = fields.find(f => normalizeFitmentKey(f) === 'model')!;
    return [identity, ...fields.filter(f => !['make', 'model'].includes(normalizeFitmentKey(f)))];
};

export const formatVehicleYears = (years: string[]) => {
    const sorted = Array.from(new Set(years)).sort((a, b) => Number(a) - Number(b));
    const ranges: string[] = [];
    for (let i = 0; i < sorted.length; i++) {
        const start = sorted[i];
        let end = start;
        while (i + 1 < sorted.length && /^\d{4}$/.test(end) && Number(sorted[i + 1]) === Number(end) + 1) {
            end = sorted[++i];
        }
        ranges.push(start === end ? start || '—' : `${start}–${end}`);
    }
    return ranges.join(', ');
};

type Props = {
    vehicles: Vehicle[];
    groups?: number[][] | null;
    fields: FitmentFieldDefinition[];
    isSelected: (id: string) => boolean;
    onSelect: (ids: string[], checked: boolean) => void;
    onEdit: (vehicle: Vehicle) => void;
    onEditGroup: (records: Vehicle[]) => void;
    onProducts: (vehicle: Vehicle) => void;
    expanded: Set<string>;
    onToggle: (key: string) => void;
};

/** Table rows for the vehicle catalog; year-range groups expand into one row per year. */
export function VehicleCatalogRows({ vehicles, groups, fields, isSelected, onSelect, onEdit, onEditGroup, onProducts, expanded, onToggle }: Props) {
    const byId = new Map(vehicles.map(v => [v.id, v]));
    const columns = catalogColumns(fields);
    const combined = columns.length < fields.length;
    const yearField = fields.find(f => normalizeFitmentKey(f) === 'year');
    const makeField = fields.find(f => normalizeFitmentKey(f) === 'make');
    const value = (v: Vehicle, field?: FitmentFieldDefinition) => field ? getLegacyVehicleFieldValue(v, field) : '';
    const label = (v: Vehicle) => fields.map(f => value(v, f)).filter(Boolean).join(' ');
    const groupLabel = (v: Vehicle) => fields.filter(f => f !== yearField).map(f => value(v, f)).filter(Boolean).join(' ');
    const products = (v: Vehicle) => v.assignedProducts > 0 ? (
        <s-link accessibilityLabel={`Open assigned products for ${label(v)}`} onClick={() => onProducts(v)}>
            {v.assignedProducts.toLocaleString()}
        </s-link>
    ) : <s-text color="subdued">None</s-text>;

    return <>{(groups ?? vehicles.map(v => [v.id])).map(ids => {
        const records = ids.map(id => byId.get(id)).filter((v): v is Vehicle => !!v);
        if (!records.length) return null;
        const v = records[0];
        const multiple = records.length > 1;
        const key = `vehicle-years-${Math.min(...ids)}`;
        const open = expanded.has(key);
        const recordIds = records.map(r => String(r.id));
        const selectedCount = recordIds.filter(isSelected).length;
        const years = records.map(r => value(r, yearField));
        const sameCount = new Set(years).size === records.length
            && records.every(r => r.assignedProducts === v.assignedProducts);
        const actionId = `pf-vehicle-action-${key}`;
        const toggle = () => onToggle(key);
        return <React.Fragment key={key}>
            <s-table-row clickDelegate={actionId}>
                {columns.map((field, columnIndex) => {
                    const fieldKey = normalizeFitmentKey(field);
                    const text = fieldKey === 'year' && multiple ? formatVehicleYears(years) : value(v, field) || '—';
                    if (columnIndex > 0) return <s-table-cell key={fieldKey}>{text}</s-table-cell>;
                    return <s-table-cell key={fieldKey}>
                        <s-stack direction="inline" gap="base" alignItems="center">
                            <RowCheckbox
                                accessibilityLabel={`Select ${label(v)}${multiple ? `, all ${records.length} years` : ''}`}
                                checked={selectedCount === records.length}
                                indeterminate={selectedCount > 0 && selectedCount < records.length}
                                onChange={checked => onSelect(recordIds, checked)}
                            />
                            {combined && fieldKey === 'model' ? (
                                <s-stack gap="none">
                                    <s-text fontWeight="semibold">{text}</s-text>
                                    <s-text color="subdued">{value(v, makeField) || '—'}</s-text>
                                </s-stack>
                            ) : <s-text fontWeight="semibold">{text}</s-text>}
                        </s-stack>
                    </s-table-cell>;
                })}
                <s-table-cell>
                    {!multiple ? products(v) : (
                        <s-link accessibilityLabel={`${open ? 'Hide' : 'View'} assigned products by year for ${label(v)}`} onClick={toggle}>
                            {sameCount ? `${v.assignedProducts.toLocaleString()} per year` : 'View by year'}
                        </s-link>
                    )}
                </s-table-cell>
                <s-table-cell>
                    {multiple ? (
                        <s-stack direction="inline" gap="small-200" alignItems="center">
                            {/* System vehicles can't be renamed in place, so they keep the per-year copy flow. */}
                            {records.every(r => r.isCustom) && (
                                <s-button variant="tertiary" accessibilityLabel={`Edit all years of ${groupLabel(v)}`} onClick={() => onEditGroup(records)}>
                                    Edit
                                </s-button>
                            )}
                            <s-button id={actionId} variant="tertiary" icon={open ? 'chevron-up' : 'chevron-down'}
                                accessibilityLabel={`${open ? 'Hide' : 'View'} years for ${label(v)}`} onClick={toggle}>
                                {open ? 'Hide years' : 'View years'}
                            </s-button>
                        </s-stack>
                    ) : (
                        <s-button id={actionId} variant="tertiary" accessibilityLabel={`Edit ${label(v)}`} onClick={() => onEdit(v)}>
                            Edit
                        </s-button>
                    )}
                </s-table-cell>
            </s-table-row>
            {multiple && open && [...records]
                .sort((a, b) => Number(value(b, yearField)) - Number(value(a, yearField)))
                .map(vehicle => {
                    const id = String(vehicle.id);
                    const editId = `pf-vehicle-edit-${vehicle.id}`;
                    return <s-table-row key={id} clickDelegate={editId}>
                        {columns.map((field, columnIndex) => {
                            const fieldKey = normalizeFitmentKey(field);
                            return <s-table-cell key={fieldKey}>
                                {columnIndex === 0 ? (
                                    <s-box paddingInlineStart="large">
                                        <s-stack direction="inline" gap="base" alignItems="center">
                                            <RowCheckbox
                                                accessibilityLabel={`Select ${label(vehicle)}`}
                                                checked={isSelected(id)}
                                                onChange={checked => onSelect([id], checked)}
                                            />
                                            {fieldKey === 'year' && (value(vehicle, yearField) || '—')}
                                        </s-stack>
                                    </s-box>
                                ) : fieldKey === 'year' ? value(vehicle, yearField) || '—' : null}
                            </s-table-cell>;
                        })}
                        <s-table-cell>{products(vehicle)}</s-table-cell>
                        <s-table-cell>
                            <s-button id={editId} variant="tertiary" accessibilityLabel={`Edit ${label(vehicle)}`} onClick={() => onEdit(vehicle)}>
                                Edit
                            </s-button>
                        </s-table-cell>
                    </s-table-row>;
                })}
        </React.Fragment>;
    })}</>;
}
